import Eyebrow from "@/components/base/Eyebrow";
import MicroLabel from "@/components/base/MicroLabel";
import Reveal from "@/components/base/Reveal";

interface PairSide {
  term: string;
  kicker: string;
  text: string;
}

interface Pair {
  left: PairSide;
  right: PairSide;
  note: string;
}

const PAIRS: Pair[] = [
  {
    left: {
      term: "Market order",
      kicker: "Fills now",
      text: "Takes the next price available in the market. Speed is the whole point — the price you clicked is a reference, not a promise.",
    },
    right: {
      term: "Limit order",
      kicker: "Fills at your price",
      text: "Waits until the market comes to you. Your worst price is locked in, but nothing guarantees the market ever arrives.",
    },
    note: "Ask which one you would rather give up: the moment, or the level. Getting in today matters more than the last pip — market. The level is the reason for the trade — limit.",
  },
  {
    left: {
      term: "Stop order",
      kicker: "Guarantees the exit",
      text: "Triggers at your level, then becomes a market order. You are out of the position, but the fill can land well past the trigger in a fast move.",
    },
    right: {
      term: "Stop-limit order",
      kicker: "Guarantees the price",
      text: "Triggers at your level, then places a limit order. You will never fill worse than your limit — and in a gap, you may not fill at all.",
    },
    note: "The question to ask before you place either: which failure can I live with — a worse price, or a position I could not exit?",
  },
  {
    left: {
      term: "Spread",
      kicker: "Quoted before you click",
      text: "The standing gap between bid and ask. You know the number in advance, and it is charged on every market order you send.",
    },
    right: {
      term: "Slippage",
      kicker: "Revealed after the fill",
      text: "The gap between the price you expected and the price you got. It only exists once quotes move faster than your order travels.",
    },
    note: "Do not add one to the other. When the spread widens in a fast market, that extra cost is already sitting inside your slippage figure.",
  },
  {
    left: {
      term: "Stop-loss",
      kicker: "Fixed level",
      text: "Sits at one price and stays there, hit or moved by you. It caps how much a single bad trade can cost.",
    },
    right: {
      term: "Trailing stop",
      kicker: "Follows price",
      text: "Keeps a fixed distance behind price and only ever moves in your favour. It ratchets forward and never back.",
    },
    note: "A stop-loss protects the money you started with. A trailing stop protects the profit you have already built — and can cost you a little of it when price reverses.",
  },
];

function PairPanel({ side }: { side: PairSide }) {
  return (
    <div className="p-5 md:p-6 border-b-2 md:border-b-0 md:border-r-2 border-foreground-950">
      <MicroLabel tone="primary">{side.kicker}</MicroLabel>
      <h3 className="mt-3 font-heading font-black uppercase tracking-tight text-lg md:text-xl text-foreground-950">
        {side.term}
      </h3>
      <p className="mt-2 text-xs md:text-sm leading-relaxed text-foreground-600">{side.text}</p>
    </div>
  );
}

export default function ConfusedPairs() {
  return (
    <section className="px-4 md:px-8 py-16 md:py-24 bg-background-100 border-y-2 border-foreground-950">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <Eyebrow>Commonly confused</Eyebrow>
          <span className="h-0.5 flex-1 bg-foreground-950"></span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-end">
          <h2 className="lg:col-span-7 font-heading font-black uppercase leading-[0.92] tracking-tight text-foreground-950 text-3xl md:text-5xl">
            Four pairs that sound alike
          </h2>
          <p className="lg:col-span-5 text-sm md:text-base leading-relaxed text-foreground-600">
            Every expensive mistake in order placement starts with mixing up two terms. Here is the one line that
            separates each pair, in plain words you can hold onto.
          </p>
        </div>

        <div className="mt-10 flex flex-col gap-4">
          {PAIRS.map((pair, index) => (
            <Reveal key={pair.left.term}>
              <article className="border-2 border-foreground-950 bg-background-50">
                <div className="grid grid-cols-1 md:grid-cols-12">
                  <div className="md:col-span-5 relative">
                    <span className="absolute top-5 right-5 md:top-6 md:right-6 font-label text-[10px] uppercase tracking-[0.18em] text-foreground-400">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <PairPanel side={pair.left} />
                  </div>
                  <div className="hidden md:flex md:col-span-2 items-center justify-center border-r-2 border-foreground-950">
                    <span className="w-11 h-11 flex items-center justify-center border-2 border-foreground-950 bg-accent-500 font-heading font-black text-xs text-foreground-950">
                      VS
                    </span>
                  </div>
                  <div className="md:col-span-5">
                    <PairPanel side={pair.right} />
                  </div>
                </div>
                <div className="border-t-2 border-foreground-950 bg-background-50 px-5 md:px-6 py-4 flex flex-col md:flex-row md:items-baseline gap-2 md:gap-4">
                  <MicroLabel mark="arrow" tone="muted" className="shrink-0">
                    What separates them
                  </MicroLabel>
                  <p className="text-xs md:text-sm leading-relaxed text-foreground-700">{pair.note}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}